import { sendUnderContractEmail } from '../services/emailSend';
import { startDealCheckIns } from '../services/checkInService';
import MoneyInput from '../components/MoneyInput';
import React, { useState, useEffect, useRef } from 'react';
import { DealColorPicker, DealTag } from '../components/DealColorPicker';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import TcAgentPicker, { isTcUser, withTcFields, loadDealAgents } from '../components/TcAgentPicker';
import PhoneInput from '../components/PhoneInput';
// import getTransactionById - removed
import { generateTasksForTransaction } from '../services/taskService';
import { generateBuyerTasks, syncTaskDates } from '../services/transactionTasks';
import { createTransaction, getTransaction, updateTransaction } from '../services/transactionService';
// import notificationTriggerService - removed
import { ArrowLeft, DollarSign, FileText } from 'lucide-react';
import { formatFieldValue } from '../utils/formatters';
import { getAgents } from '../services/userService';
import { formatPhone } from '../utils/formatters';
import LenderSection from '../components/LenderSection';
import TitleAgentSection from '../components/TitleAgentSection';
import SourceSelect from '../components/SourceSelect';
import { useContactPrefill, linkContactToTransaction, usePipelineSource, markPipelineConverted } from '../services/contactLink';
import ClientPicker, { resolveClientContact } from '../components/ClientPicker';
import { businessDayError } from '../shared/deadlines';
import DeadlineDays, { applyDeadlineDays } from '../components/DeadlineDays';
import ContractImport, { ContractDetails } from '../components/ContractImport';
import DocChecklistUpload, { uploadPendingDocs } from '../components/DocChecklistUpload';
import { BUYER_DOCS, missingDocs, missingDocsMessage } from '../shared/transactionDocs';
import { listDealDocuments } from '../services/dealDocuments';

// Deadlines that can be set as N days from the Under Contract (Effective) Date.
const DAY_FIELDS = ['closingDate', 'firstEscrowDueDate', 'secondEscrowDueDate', 'homeInspectionDeadline', 'appraisalDeadline', 'homeSaleContingencyDate', 'financingDeadline'];

// Weekend / US bank holiday check now lives in src/shared/deadlines.js (also used for deadline day-counting).
const getClosingDateError = (dateStr) => businessDayError(dateStr, 'Anticipated closing date');

// Stops a save from spinning forever when the database connection stalls.
const withTimeout = (promise, ms, label) => {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(`Timed out while ${label}. Check Transactions before trying again, since the deal may have partly saved.`)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
};

export default function BuyerForm() {
  const { id: editId } = useParams();
  const isEdit = !!editId;
  const [editLoaded, setEditLoaded] = useState(!editId);
  const navigate = useNavigate();
  const { user, userRole } = useAuth();
  // TC only (new deals): the deal belongs to the agent chosen at the top of the form.
  const isTC = isTcUser(userRole) && !isEdit;
  const [tcPick, setTcPick] = useState({ agentUid: '', coAgentUid: '' });
  const [tcSubmitTried, setTcSubmitTried] = useState(false);
  const [tcAgents, setTcAgents] = useState([]);
  useEffect(() => { if (isTC) loadDealAgents().then(setTcAgents).catch(() => setTcAgents([])); }, [isTC]);
  const ownerUid = isTC ? tcPick.agentUid : user?.uid;
  const tcError = isTC && !tcPick.agentUid ? 'Choose the agent this deal is for' : '';
  const asTc = (payload) => (isTC ? withTcFields(payload, 'Buyer', tcPick, tcAgents, { uid: user?.uid, name: user?.displayName || '', email: user?.email || '' }) : payload);
  const [wasDraft, setWasDraft] = useState(false);
  const [draftSaving, setDraftSaving] = useState(false);
  const [picked, setPicked] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [pendingDocs, setPendingDocs] = useState({});
  const [savedDocs, setSavedDocs] = useState([]);
  const [docErrors, setDocErrors] = useState(false);
  const [additionalContacts, setAdditionalContacts] = useState([]);
  const [coSellingAgents, setCoSellingAgents] = useState([]);
  const [agents, setAgents] = useState([]);
  const [closingDateError, setClosingDateError] = useState('');

  useEffect(() => {
    getAgents()
      .then(setAgents)
      .catch(err => console.error('Could not load agents:', err));
  }, []);

  const emptyContact = { firstName: '', lastName: '', role: '', phone: '', email: '', signer: true };

  const addContact = () => {
    setAdditionalContacts(prev => [...prev, { ...emptyContact, id: Date.now() }]);
  };

  const updateContact = (id, field, value) => {
    const nextValue = field === 'phone' ? formatPhone(value) : value;
    setAdditionalContacts(prev => prev.map(c => (c.id === id ? { ...c, [field]: nextValue } : c)));
  };

  const removeContact = (id) => {
    setAdditionalContacts(prev => prev.filter(c => c.id !== id));
  };

  const addCoSellingAgent = () => {
    setCoSellingAgents(prev => [...prev, { id: Date.now(), uid: '' }]);
  };

  const updateCoSellingAgent = (id, uid) => {
    setCoSellingAgents(prev => prev.map(a => (a.id === id ? { ...a, uid } : a)));
  };

  const removeCoSellingAgent = (id) => {
    setCoSellingAgents(prev => prev.filter(a => a.id !== id));
  };
  const [formData, setFormData] = useState({
    // Basic Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    
    // Property Information
    propertyStatus: '', // 'new-construction' or 'resale'
    propertyAddress: '',
    dealColor: '',
    city: '',
    state: '',
    zipCode: '',
    firstEscrowAmount: '',
    firstEscrowDueDate: '',
    secondEscrowAmount: '',
    secondEscrowDueDate: '',
    propertyType: 'Single Family',
    
    // Buyer Details
    sellerName: '',
    sellerAgent: '',
    sellerAgentPhone: '',
    sellerAgentEmail: '',
    sellerAgentTCName: '',
    sellerAgentTCPhone: '',
    sellerAgentTCEmail: '',
    builderName: '',
    communityName: '',
    floorplan: '',
    builderRepFirstName: '',
    builderRepLastName: '',
    builderRepPhone: '',
    builderRepEmail: '',
    basePrice: '',
    builderClosingCostAmount: '',
    commissionBase: '', // 'Contract Price' or 'Base Price' (new construction)
    reduceBaseByBuilderCC: '', // 'Yes' or 'No' (new construction)
    builderBonusType: '', // 'percentage', 'flat' or 'none'
    builderBonusPercent: '',
    builderBonusAmount: '',
    
    // Offer & Contract
    offerPrice: '',
    contractDate: '',
    closingDate: '',
    
    // Financial Details
    
    // Contingencies
    homeInspection: false,
    appraisal: false,
    financing: false,
    homeInspectionDeadline: '',
    appraisalDeadline: '',
    homeSaleContingencyDate: '',
    homeSaleContingency: '', // 'Yes' or 'No' (required, no default)
    homeSaleAgentFirstName: '',
    homeSaleAgentLastName: '',
    homeSaleAgentEmail: '',
    homeSaleAgentPhone: '',
    financingDeadline: '',
    associationApprovalRequired: '',
    paymentType: '',
    lender: {},
    financingSource: '', // 'Builder lender' or 'Outside lender'
    
    // Commission Information
    commissionType: 'percentage', // 'percentage' or 'dollar'
    commissionPercentage: '',
    commissionDollarAmount: '',
    leadSource: 'agent-generated', // 'agent-generated' or 'team-generated'
    source: '',
    sourceOther: '',

    // Additional Notes
    notes: ''
  });
  const linkedContact = useContactPrefill(setFormData, !editId);
  const pipelineSource = usePipelineSource(setFormData, !editId);

  // Financing deadline defaults to 30 days after the under contract date, until the user types a date.
  const financingTouched = useRef(false);
  useEffect(() => {
    if (formData.paymentType !== 'Financing' || !formData.contractDate || financingTouched.current) return;
    const d = new Date(`${formData.contractDate}T00:00:00`);
    if (isNaN(d)) return;
    d.setDate(d.getDate() + 30);
    const v = d.toISOString().slice(0, 10);
    setFormData(prev => (prev.financingDeadline === v ? prev : { ...prev, financingDeadline: v }));
  }, [formData.contractDate, formData.paymentType]);

  // Contract reader: fill only empty fields; day counts go through the Days inputs so dates follow the Effective Date.
  const [fromContract, setFromContract] = useState([]);
  const applyContract = (f, mapped) => {
    const filled = [];
    setFormData((prev) => {
      const next = { ...prev };
      const put = (k, v) => { if (v !== undefined && v !== null && v !== '' && !prev[k]) { next[k] = String(v); filled.push(k); } };
      if (f.buyerName && !prev.firstName && !prev.lastName) { const parts = f.buyerName.split(/\s+/); put('lastName', parts.pop()); put('firstName', parts.join(' ')); }
      ['sellerName', 'propertyAddress', 'offerPrice', 'firstEscrowAmount', 'secondEscrowAmount', 'contractDate', 'closingDate', 'paymentType'].forEach((k) => put(k, f[k]));
      if (mapped && Object.keys(mapped.extras).length) { next.contractDetails = { ...Object.fromEntries(Object.entries(mapped.extras).map(([k, v]) => [k, String(v)])), ...(prev.contractDetails || {}) }; filled.push('contractDetails'); }
      DAY_FIELDS.forEach((k) => { if (f[`${k}Days`] && !prev[k] && !prev[`${k}Days`]) { next[`${k}Days`] = String(f[`${k}Days`]); next[`${k}DayMode`] = 'calendar'; filled.push(k); } });
      return applyDeadlineDays(next, DAY_FIELDS, 'contractDate');
    });
    setFromContract(filled);
    return filled;
  };
  const reviewStyle = (k) => (fromContract.includes(k) ? { background: '#fff6d6' } : null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'financingDeadline') financingTouched.current = true;
    setFormData(prev => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : formatFieldValue(name, value) };
      if (name === 'contractDate') return applyDeadlineDays(next, DAY_FIELDS, 'contractDate');
      if (DAY_FIELDS.includes(name)) next[`${name}Days`] = ''; // a date typed by hand replaces the day count
      return next;
    });
  };
  const changeDays = (name, days, mode) => {
    if (name === 'financingDeadline') financingTouched.current = true;
    setFormData(prev => applyDeadlineDays({ ...prev, [`${name}Days`]: days, [`${name}DayMode`]: mode }, DAY_FIELDS, 'contractDate'));
  };
  const daysFor = (name) => <DeadlineDays name={name} values={formData} effectiveDate={formData.contractDate} onChange={changeDays} inputStyle={styles.input} />;

  // Edit mode (route /transactions/:id/edit): load the saved deal into the form.
  useEffect(() => {
    if (!isEdit || !user) return;
    (async () => {
      try {
        const tx = await getTransaction(ownerUid, editId);
        if (tx) {
          financingTouched.current = true;
          setFormData(prev => ({
            ...prev,
            ...Object.fromEntries(Object.keys(prev).filter(k => tx[k] !== undefined).map(k => [k, tx[k]])),
          }));
          setAdditionalContacts(tx.additionalContacts || []);
          setCoSellingAgents((tx.coSellingAgents || []).map(a => ({ uid: a.uid })));
          setWasDraft(!!tx.isDraft);
        }
      } catch (err) {
        console.error('Could not load transaction for editing', err);
        setSubmitError('Could not load this transaction.');
      } finally {
        setEditLoaded(true);
      }
    })();
  }, [isEdit, editId, user]);

  // Save as draft: no required fields, no tasks, no emails. "Continue draft" on the deal page reopens it here.
  useEffect(() => {
    if (editId && ownerUid) listDealDocuments(ownerUid, editId).then(setSavedDocs).catch(() => setSavedDocs([]));
  }, [editId, ownerUid]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveDraft = async () => {
    if (!user) return;
    if (isTC && !tcPick.agentUid) { setTcSubmitTried(true); window.scrollTo(0, 0); return; }
    setDraftSaving(true);
    setSubmitError(null);
    try {
      const payload = { ...formData, status: 'draft', isDraft: true, additionalContacts, coSellingAgents: coSellingAgents.filter(a => a.uid).map(a => { const agent = agents.find(x => x.uid === a.uid); return { uid: a.uid, name: agent?.displayName || '', email: agent?.email || '' }; }) };
      let txId = editId;
      if (isEdit) await updateTransaction(ownerUid, editId, payload);
      else txId = await createTransaction(ownerUid, asTc(payload), 'Buyer');
      await uploadPendingDocs(ownerUid, txId, pendingDocs, user.displayName || user.email || '');
      navigate(`/transactions/${txId}`, { state: { saved: 'Draft saved. No tasks or emails were created. Use Continue draft to finish it.' } });
    } catch (err) {
      console.error('Could not save draft', err);
      setSubmitError('Could not save the draft. Please try again.');
    } finally {
      setDraftSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isTC && !tcPick.agentUid) { setTcSubmitTried(true); window.scrollTo(0, 0); return; }
    const dateError = getClosingDateError(formData.closingDate);
    setClosingDateError(dateError);
    if (dateError) return;
    // New buyer (under contract) deal: every required document must be attached first.
    if (!isEdit || wasDraft) {
      const missingD = missingDocs(BUYER_DOCS, pendingDocs, savedDocs);
      if (missingD.length) { setDocErrors(true); setSubmitError(missingDocsMessage(missingD)); window.scrollTo(0, 0); return; }
    }
    setSubmitError(null);
    setLoading(true);

    try {
      if (!user) throw new Error('User not authenticated');
      if (!formData.closingDate) throw new Error('Closing date is required for task generation');

      // Edit mode: save changes, then move the task dates to match the current deal dates.
      if (isEdit && !wasDraft) {
        await updateTransaction(ownerUid, editId, {
          ...formData,
          additionalContacts,
          coSellingAgents: coSellingAgents
            .filter(a => a.uid)
            .map(a => {
              const agent = agents.find(x => x.uid === a.uid);
              return { uid: a.uid, name: agent?.displayName || '', email: agent?.email || '' };
            }),
        });
        const fresh = await getTransaction(ownerUid, editId);
        await syncTaskDates(editId, fresh);
        navigate(`/transactions/${editId}`, { state: { saved: 'Changes saved.' } });
        return;
      }

      // Create the transaction and get its ID
      console.log('[BuyerForm] step 1: creating transaction');
      const buyerPayload = {
        ...formData,
        status: formData.status && formData.status !== 'draft' ? formData.status : 'under-contract',
        isDraft: false,
        additionalContacts,
        coSellingAgents: coSellingAgents
          .filter(a => a.uid)
          .map(a => {
            const agent = agents.find(x => x.uid === a.uid);
            return { uid: a.uid, name: agent?.displayName || '', email: agent?.email || '' };
          }),
      };
      let transactionId = editId;
      if (wasDraft) await withTimeout(updateTransaction(ownerUid, editId, buyerPayload), 20000, 'saving the transaction');
      else transactionId = await withTimeout(createTransaction(ownerUid, asTc(buyerPayload), 'Buyer'), 20000, 'saving the transaction');
      const clientContact = await resolveClientContact(linkedContact || (picked?.contactId ? { id: picked.contactId } : null), formData, ownerUid, userRole === 'Admin', 'Buyer');
      await markPipelineConverted(pipelineSource || picked?.source, 'Buyer', transactionId, ownerUid);
      await linkContactToTransaction(clientContact, ownerUid, transactionId, 'Buyer', [formData.firstName, formData.lastName].filter(Boolean).join(' '));

      // Generate tasks based on closing date
      if (transactionId) {
        console.log('[BuyerForm] step 2: creating tasks for', transactionId);
        await withTimeout(generateBuyerTasks(transactionId, formData, ownerUid), 60000, 'creating the tasks');
        // After the tasks exist, so matching "upload" tasks can be completed.
        await withTimeout(uploadPendingDocs(ownerUid, transactionId, pendingDocs, user.displayName || user.email || ''), 120000, 'uploading the documents');
        // A buyer deal created under contract: send the Internal NEW Contract email now (server checks the date is recent, once per deal).
        if (formData.underContractDate) await sendUnderContractEmail(transactionId);
        if (formData.underContractDate) await startDealCheckIns(transactionId, ownerUid, user?.uid);
      }

      // Show success message and redirect
      navigate(`/transactions/${transactionId}`, { state: { saved: 'Buyer transaction saved. Its tasks were created.' } });
    } catch (error) {
      console.error('Error saving buyer transaction:', error);
      setSubmitError(error.message || 'Failed to save transaction');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/transactions');
  };

  if (isEdit && !editLoaded) return <div style={{ padding: 24 }}>Loading transaction...</div>;

  return (
    <div style={styles.container}>
      <button style={styles.backButton} onClick={handleCancel}>
        <ArrowLeft size={20} /> Back
      </button>

      <h1 style={styles.title}>Buyer Transaction Form</h1>

      {submitError && (
        <div style={styles.errorBox}>
          <p style={styles.errorText}>Error: {submitError}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} style={styles.form}>
          <ContractImport kind="Buyer" onFields={applyContract} />
          {(!isEdit || wasDraft) && <DocChecklistUpload title="Buyer documents" docs={BUYER_DOCS} pending={pendingDocs} onChange={setPendingDocs} existing={savedDocs} showErrors={docErrors} />}
          <ContractDetails value={formData.contractDetails} onChange={(v) => setFormData(prev => ({ ...prev, contractDetails: v }))} />
          {isTC && <TcAgentPicker value={tcPick} onChange={setTcPick} agents={tcAgents} error={tcSubmitTried ? tcError : ''} />}
        <DealColorPicker value={formData.dealColor} onChange={(c) => setFormData(prev => ({ ...prev, dealColor: c }))} />
        {/* Basic Information */}
        <fieldset style={styles.fieldset}>
          <legend style={styles.legend}>Buyer Information</legend>
          {!isEdit && <ClientPicker uid={ownerUid} isAdmin={userRole === 'Admin'} withAddress={false} viewAll={isTC} onPick={(p) => { setPicked(p); setFormData(prev => ({ ...prev, ...p.fields })); if (p.additionalContacts?.length) setAdditionalContacts((prev) => (prev.length ? prev : p.additionalContacts.map((c, n) => ({ id: Date.now() + n, firstName: c.firstName, lastName: c.lastName, role: c.relationship, phone: c.phone, email: c.email, signer: true })))); }} />}
          <div style={styles.inputGroup}>
            <label style={styles.label}>First Name</label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              style={{ ...styles.input, ...reviewStyle('firstName') }}
            />
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Last Name</label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              style={{ ...styles.input, ...reviewStyle('lastName') }}
            />
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              style={styles.input}
            />
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Phone</label>
            <PhoneInput
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              style={styles.input}
            />
          </div>
        </fieldset>

        {/* Property Information */}
        <fieldset style={styles.fieldset}>
          <legend style={styles.legend}>Additional Contacts</legend>
          <button type="button" onClick={addContact} style={{padding: '8px 16px', backgroundColor: '#D4AF37', color: '#000', border: 'none', borderRadius: '4px', fontWeight: '600', cursor: 'pointer', marginBottom: '12px'}}>
            + Add Contact
          </button>
          {additionalContacts.length === 0 && <p style={styles.label}>No additional contacts yet.</p>}
          {additionalContacts.map((contact, idx) => (
            <div key={contact.id} style={{border: '1px solid #DDD', borderRadius: '6px', padding: '16px', marginBottom: '16px'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px'}}>
                <span style={{display: 'flex', alignItems: 'center', gap: '16px'}}>
                  <strong>Contact {idx + 2}</strong>
                  <label style={{display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 400, cursor: 'pointer'}} title="Uncheck if this person does not sign. They still get the transaction emails.">
                    <input type="checkbox" checked={contact.signer !== false} onChange={(e) => updateContact(contact.id, 'signer', e.target.checked)} /> Signer
                  </label>
                </span>
                <button type="button" onClick={() => removeContact(contact.id)} style={{background: 'none', border: 'none', color: '#d32f2f', cursor: 'pointer', fontWeight: '600'}}>Remove</button>
              </div>
              <div style={styles.twoColumn}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>First Name</label>
                  <input type="text" value={contact.firstName} onChange={(e) => updateContact(contact.id, 'firstName', e.target.value)} style={styles.input} />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Last Name</label>
                  <input type="text" value={contact.lastName} onChange={(e) => updateContact(contact.id, 'lastName', e.target.value)} style={styles.input} />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Role / Relationship</label>
                  <input type="text" value={contact.role} onChange={(e) => updateContact(contact.id, 'role', e.target.value)} placeholder="Spouse, co-buyer, attorney, etc." style={styles.input} />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Phone</label>
                  <PhoneInput value={contact.phone} onChange={(e) => updateContact(contact.id, 'phone', e.target.value)} style={styles.input} />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Email</label>
                  <input type="email" value={contact.email} onChange={(e) => updateContact(contact.id, 'email', e.target.value)} style={styles.input} />
                </div>
              </div>
            </div>
          ))}
        </fieldset>

        <fieldset style={styles.fieldset}>
          <legend style={styles.legend}>Co-Selling Agents</legend>
          <button type="button" onClick={addCoSellingAgent} style={{padding: '8px 16px', backgroundColor: '#D4AF37', color: '#000', border: 'none', borderRadius: '4px', fontWeight: '600', cursor: 'pointer', marginBottom: '12px'}}>
            + Add Co-Selling Agent
          </button>
          {coSellingAgents.length === 0 && <p style={styles.label}>No co-selling agent on this transaction.</p>}
          {coSellingAgents.map((coAgent, idx) => (
            <div key={coAgent.id} style={{display: 'flex', gap: '12px', alignItems: 'flex-end', marginBottom: '12px'}}>
              <div style={{...styles.inputGroup, flex: 1}}>
                <label style={styles.label}>Co-Selling Agent {idx + 1}</label>
                <select value={coAgent.uid} onChange={(e) => updateCoSellingAgent(coAgent.id, e.target.value)} style={styles.input}>
                  <option value="">Select an agent</option>
                  {agents.map(agent => (
                    <option key={agent.uid} value={agent.uid}>{agent.displayName}</option>
                  ))}
                </select>
              </div>
              <button type="button" onClick={() => removeCoSellingAgent(coAgent.id)} style={{background: 'none', border: 'none', color: '#d32f2f', cursor: 'pointer', fontWeight: '600', paddingBottom: '10px'}}>
                Remove
              </button>
            </div>
          ))}
        </fieldset>

        <fieldset style={styles.fieldset}>
          <legend style={styles.legend}>Property Information</legend>
          <div style={styles.inputGroup}>
            <label style={styles.label}>New Construction or Resale</label>
            <div style={styles.radioGroup}>
              <label style={styles.radioLabel}>
                <input
                  type="radio"
                  name="propertyStatus"
                  value="new-construction"
                  checked={formData.propertyStatus === 'new-construction'}
                  onChange={handleChange}
                />
                New Construction
              </label>
              <label style={styles.radioLabel}>
                <input
                  type="radio"
                  name="propertyStatus"
                  value="resale"
                  checked={formData.propertyStatus === 'resale'}
                  onChange={handleChange}
                />
                Resale
              </label>
            </div>
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Property Address</label>
            <input
              type="text"
              name="propertyAddress"
              value={formData.propertyAddress}
              onChange={handleChange}
              style={{ ...styles.input, ...reviewStyle('propertyAddress') }}
            />
          </div>
          <div style={styles.twoColumn}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>City</label>
              <input type="text" name="city" value={formData.city} onChange={handleChange} style={styles.input} />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>State</label>
              <input type="text" name="state" value={formData.state} onChange={handleChange} style={styles.input} />
            </div>
          </div>
          <div style={styles.twoColumn}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Zip Code</label>
              <input type="text" name="zipCode" value={formData.zipCode} onChange={handleChange} style={styles.input} />
            </div>
          </div>
          <div style={styles.twoColumn}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Property Type</label>
              <select name="propertyType" value={formData.propertyType} onChange={handleChange} style={styles.input}>
                <option>Single Family</option>
                <option>Condo</option>
                <option>Townhouse</option>
                <option>Multi-Family</option>
                <option>Land</option>
                <option>Other</option>
              </select>
            </div>
          </div>
        </fieldset>

        {formData.propertyStatus === 'new-construction' && (
          <>
            <fieldset style={styles.fieldset}>
              <legend style={styles.legend}>New Construction Details</legend>
              <div style={styles.twoColumn}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Builder</label>
                  <input type="text" name="builderName" value={formData.builderName} onChange={handleChange} style={styles.input} />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Community</label>
                  <input type="text" name="communityName" value={formData.communityName} onChange={handleChange} style={styles.input} />
                </div>
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Floorplan</label>
                <input type="text" name="floorplan" value={formData.floorplan} onChange={handleChange} style={styles.input} />
              </div>
            </fieldset>

            <fieldset style={styles.fieldset}>
              <legend style={styles.legend}>Builder Representative</legend>
              <div style={styles.twoColumn}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Builder Rep's First Name</label>
                  <input type="text" name="builderRepFirstName" value={formData.builderRepFirstName} onChange={handleChange} style={styles.input} />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Builder Rep's Last Name</label>
                  <input type="text" name="builderRepLastName" value={formData.builderRepLastName} onChange={handleChange} style={styles.input} />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Builder Rep's Phone</label>
                  <PhoneInput name="builderRepPhone" value={formData.builderRepPhone} onChange={handleChange} style={styles.input} />
                </div>
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Builder Rep's Email</label>
                <input type="email" name="builderRepEmail" value={formData.builderRepEmail} onChange={handleChange} style={styles.input} />
              </div>
            </fieldset>
          </>
        )}

        {/* Seller & Agent Information */}
        {formData.propertyStatus !== 'new-construction' && (
        <fieldset style={styles.fieldset}>
          <legend style={styles.legend}>Seller & Agent Information</legend>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Seller Names</label>
            <input
              type="text"
              name="sellerName"
              value={formData.sellerName}
              onChange={handleChange}
              style={{ ...styles.input, ...reviewStyle('sellerName') }}
            />
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Seller's Agent</label>
            <input
              type="text"
              name="sellerAgent"
              value={formData.sellerAgent}
              onChange={handleChange}
              style={styles.input}
            />
          </div>
          <div style={styles.twoColumn}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Seller's Agent Phone</label>
              <PhoneInput
                name="sellerAgentPhone"
                value={formData.sellerAgentPhone}
                onChange={handleChange}
                style={styles.input}
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Seller's Agent Email</label>
              <input
                type="email"
                name="sellerAgentEmail"
                value={formData.sellerAgentEmail}
                onChange={handleChange}
                style={styles.input}
              />
            </div>
          </div>
          <div style={styles.subheading}>Seller's Agent Transaction Coordinator (Optional)</div>
          <div style={styles.twoColumn}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>TC Name</label>
              <input
                type="text"
                name="sellerAgentTCName"
                value={formData.sellerAgentTCName}
                onChange={handleChange}
                style={styles.input}
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>TC Phone</label>
              <PhoneInput
                name="sellerAgentTCPhone"
                value={formData.sellerAgentTCPhone}
                onChange={handleChange}
                style={styles.input}
              />
            </div>
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>TC Email</label>
            <input
              type="email"
              name="sellerAgentTCEmail"
              value={formData.sellerAgentTCEmail}
              onChange={handleChange}
              style={styles.input}
            />
          </div>
        </fieldset>
        )}

        {/* Offer & Contract Timeline */}
        <fieldset style={styles.fieldset}>
          <legend style={styles.legend}>Offer & Contract Timeline</legend>
          <div style={styles.twoColumn}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Contract Price</label>
              <MoneyInput
                name="offerPrice"
                value={formData.offerPrice}
                onChange={handleChange}
                style={{ ...styles.input, ...reviewStyle('offerPrice') }}
              />
            </div>
          </div>
          {formData.propertyStatus === 'new-construction' && (
            <div style={styles.twoColumn}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Base Price</label>
                <MoneyInput name="basePrice" value={formData.basePrice} onChange={handleChange} style={styles.input} />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Closing Costs Paid by Builder</label>
                <MoneyInput name="builderClosingCostAmount" value={formData.builderClosingCostAmount} onChange={handleChange} style={styles.input} />
              </div>
            </div>
          )}
          <div style={styles.twoColumn}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Under Contract Date</label>
              <input
                type="date"
                name="contractDate"
                value={formData.contractDate}
                onChange={handleChange}
                style={{ ...styles.input, ...reviewStyle('contractDate') }}
              />
            </div>
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Anticipated Closing Date</label>
            <input
              type="date"
              name="closingDate"
              value={formData.closingDate}
              onChange={handleChange}
              style={{ ...styles.input, ...reviewStyle('closingDate') }}
            />
            {daysFor('closingDate')}
            {closingDateError && (
              <p style={{ color: '#FF6B6B', fontSize: '13px', margin: '4px 0 0' }}>{closingDateError}</p>
            )}
          </div>
          {formData.propertyStatus === 'resale' && (
            <>
              <div style={styles.subheading}>Escrow Deposits (Resale)</div>
              <div style={styles.twoColumn}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>First Escrow Deposit Amount</label>
                  <MoneyInput name="firstEscrowAmount" value={formData.firstEscrowAmount} onChange={handleChange} style={{ ...styles.input, ...reviewStyle('firstEscrowAmount') }} />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>First Escrow Due Date</label>
                  <input type="date" name="firstEscrowDueDate" value={formData.firstEscrowDueDate} onChange={handleChange} style={{ ...styles.input, ...reviewStyle('firstEscrowDueDate') }} />
                  {daysFor('firstEscrowDueDate')}
                </div>
              </div>
              <div style={styles.twoColumn}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Second Escrow Deposit Amount</label>
                  <MoneyInput name="secondEscrowAmount" value={formData.secondEscrowAmount} onChange={handleChange} style={{ ...styles.input, ...reviewStyle('secondEscrowAmount') }} />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Second Escrow Due Date</label>
                  <input type="date" name="secondEscrowDueDate" value={formData.secondEscrowDueDate} onChange={handleChange} style={{ ...styles.input, ...reviewStyle('secondEscrowDueDate') }} />
                  {daysFor('secondEscrowDueDate')}
                </div>
              </div>
            </>
          )}
        </fieldset>


        {/* Payment Type */}
        <fieldset style={styles.fieldset}>
          <legend style={styles.legend}>Payment Type</legend>
          <p style={styles.label}>Is this purchase cash or financed?</p>
          <div style={{ display: 'flex', gap: '24px' }}>
            <label style={styles.checkboxLabel}>
              <input type="radio" name="paymentType" value="Cash" checked={formData.paymentType === 'Cash'} onChange={handleChange} required />
              Cash
            </label>
            <label style={styles.checkboxLabel}>
              <input type="radio" name="paymentType" value="Financing" checked={formData.paymentType === 'Financing'} onChange={handleChange} required />
              Financing
            </label>
          </div>
        </fieldset>

        {formData.paymentType === 'Financing' && formData.propertyStatus === 'new-construction' && (
          <fieldset style={styles.fieldset}>
            <legend style={styles.legend}>Lender</legend>
            <p style={styles.label}>Who is the lender?</p>
            <div style={{ display: 'flex', gap: '24px' }}>
              <label style={styles.checkboxLabel}>
                <input
                  type="radio"
                  name="financingSource"
                  value="Builder lender"
                  checked={formData.financingSource === 'Builder lender'}
                  onChange={() => setFormData(prev => ({ ...prev, financingSource: 'Builder lender', lender: {} }))}
                  required
                />
                Builder's lender
              </label>
              <label style={styles.checkboxLabel}>
                <input
                  type="radio"
                  name="financingSource"
                  value="Outside lender"
                  checked={formData.financingSource === 'Outside lender'}
                  onChange={() => setFormData(prev => ({ ...prev, financingSource: 'Outside lender' }))}
                  required
                />
                Outside lender
              </label>
            </div>
          </fieldset>
        )}

        {formData.paymentType === 'Financing' && formData.propertyStatus === 'resale' && (
          <LenderSection value={formData.lender} onChange={(lender) => setFormData(prev => ({ ...prev, lender }))} />
        )}

        {formData.paymentType === 'Financing' && formData.propertyStatus === 'new-construction' && formData.financingSource === 'Outside lender' && (
          <LenderSection value={formData.lender} onChange={(lender) => setFormData(prev => ({ ...prev, lender }))} />
        )}

        <TitleAgentSection value={formData.titleAgent} onChange={(titleAgent) => setFormData(prev => ({ ...prev, titleAgent }))} />

        {/* Contingencies */}
        <fieldset style={styles.fieldset}>
          <legend style={styles.legend}>Contingencies</legend>
          <div style={styles.twoColumn}>
            {formData.propertyStatus !== 'new-construction' && (
              <>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Home Inspection (Due Diligence) Deadline</label>
                  <input type="date" name="homeInspectionDeadline" value={formData.homeInspectionDeadline} onChange={handleChange} style={{ ...styles.input, ...reviewStyle('homeInspectionDeadline') }} />
                  {daysFor('homeInspectionDeadline')}
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Appraisal Deadline (leave blank if none)</label>
                  <input type="date" name="appraisalDeadline" value={formData.appraisalDeadline} onChange={handleChange} style={{ ...styles.input, ...reviewStyle('appraisalDeadline') }} />
                  {daysFor('appraisalDeadline')}
                </div>
              </>
            )}
            <div style={styles.inputGroup}>
              <label style={styles.label}>Is there a home to sell contingency?</label>
              <div style={{ display: 'flex', gap: '24px' }}>
                <label style={styles.checkboxLabel}>
                  <input type="radio" name="homeSaleContingency" value="Yes" checked={formData.homeSaleContingency === 'Yes'} onChange={handleChange} required />
                  Yes
                </label>
                <label style={styles.checkboxLabel}>
                  <input type="radio" name="homeSaleContingency" value="No" checked={formData.homeSaleContingency === 'No'} onChange={handleChange} required />
                  No
                </label>
              </div>
            </div>
            {formData.homeSaleContingency === 'Yes' && (
              <>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Home to Sell Contingency Date</label>
                  <input type="date" name="homeSaleContingencyDate" value={formData.homeSaleContingencyDate} onChange={handleChange} style={{ ...styles.input, ...reviewStyle('homeSaleContingencyDate') }} />
                  {daysFor('homeSaleContingencyDate')}
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Buyer's Listing Agent (on their other home): First Name</label>
                  <input type="text" name="homeSaleAgentFirstName" value={formData.homeSaleAgentFirstName} onChange={handleChange} style={styles.input} required />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Listing Agent: Last Name</label>
                  <input type="text" name="homeSaleAgentLastName" value={formData.homeSaleAgentLastName} onChange={handleChange} style={styles.input} required />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Listing Agent: Email</label>
                  <input type="email" name="homeSaleAgentEmail" value={formData.homeSaleAgentEmail} onChange={handleChange} style={styles.input} required />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Listing Agent: Phone</label>
                  <PhoneInput name="homeSaleAgentPhone" value={formData.homeSaleAgentPhone} onChange={handleChange} style={styles.input} required />
                </div>
              </>
            )}
            {formData.paymentType === 'Financing' && (
              <div style={styles.inputGroup}>
                <label style={styles.label}>Financing Deadline</label>
                <input type="date" name="financingDeadline" value={formData.financingDeadline} onChange={handleChange} style={{ ...styles.input, ...reviewStyle('financingDeadline') }} />
                  {daysFor('financingDeadline')}
              </div>
            )}
          </div>
        </fieldset>

        {/* Association Approval */}
        {formData.propertyStatus !== 'new-construction' && (
        <fieldset style={styles.fieldset}>
          <legend style={styles.legend}>Association Approval</legend>
          <p style={styles.label}>Is association approval required?</p>
          <div style={{ display: 'flex', gap: '24px' }}>
            <label style={styles.checkboxLabel}>
              <input type="radio" name="associationApprovalRequired" value="Yes" checked={formData.associationApprovalRequired === 'Yes'} onChange={handleChange} required />
              Yes
            </label>
            <label style={styles.checkboxLabel}>
              <input type="radio" name="associationApprovalRequired" value="No" checked={formData.associationApprovalRequired === 'No'} onChange={handleChange} required />
              No
            </label>
          </div>
        </fieldset>
        )}

        {/* Commission Information */}
        <fieldset style={styles.fieldset}>
          <legend style={styles.legend}>Commission Information</legend>

          {/* Commission Type */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>Commission Type</label>
            <div style={styles.radioGroup}>
              <label style={styles.radioLabel}>
                <input
                  type="radio"
                  name="commissionType"
                  value="percentage"
                  checked={formData.commissionType === 'percentage'}
                  onChange={handleChange}
                />
                Percentage of Purchase Price
              </label>
              <label style={styles.radioLabel}>
                <input
                  type="radio"
                  name="commissionType"
                  value="dollar"
                  checked={formData.commissionType === 'dollar'}
                  onChange={handleChange}
                />
                Fixed Dollar Amount
              </label>
            </div>
          </div>

          {/* Commission Percentage */}
          {formData.commissionType === 'percentage' && (
            <div style={styles.inputGroup}>
              <label style={styles.label}>Commission Percentage (%)</label>
              <input
                type="number"
                step="0.01"
                name="commissionPercentage"
                value={formData.commissionPercentage}
                onChange={handleChange}
                placeholder="e.g., 2.5"
                style={styles.input}
              />
            </div>
          )}

          {/* Commission Dollar Amount */}
          {formData.commissionType === 'dollar' && (
            <div style={styles.inputGroup}>
              <label style={styles.label}>Commission Amount ($)</label>
              <MoneyInput
                step="0.01"
                name="commissionDollarAmount"
                value={formData.commissionDollarAmount}
                onChange={handleChange}
                placeholder="e.g., 5000"
                style={styles.input}
              />
            </div>
          )}

          {/* New construction commission questions */}
          {formData.propertyStatus === 'new-construction' && (
            <>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Commission percentage is based on</label>
                <div style={styles.radioGroup}>
                  <label style={styles.radioLabel}>
                    <input type="radio" name="commissionBase" value="Contract Price" checked={formData.commissionBase === 'Contract Price'} onChange={handleChange} required />
                    Contract Price
                  </label>
                  <label style={styles.radioLabel}>
                    <input type="radio" name="commissionBase" value="Base Price" checked={formData.commissionBase === 'Base Price'} onChange={handleChange} required />
                    Base Price
                  </label>
                </div>
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Reduce the commission base by the closing costs paid by the builder?</label>
                <div style={styles.radioGroup}>
                  <label style={styles.radioLabel}>
                    <input type="radio" name="reduceBaseByBuilderCC" value="Yes" checked={formData.reduceBaseByBuilderCC === 'Yes'} onChange={handleChange} required />
                    Yes
                  </label>
                  <label style={styles.radioLabel}>
                    <input type="radio" name="reduceBaseByBuilderCC" value="No" checked={formData.reduceBaseByBuilderCC === 'No'} onChange={handleChange} required />
                    No
                  </label>
                </div>
              </div>
            </>
          )}

          {/* Source */}
          <SourceSelect formData={formData} onChange={handleChange} styles={styles} />

          {/* Lead Source */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>Lead Generated By{userRole !== 'Admin' && <span style={{ fontWeight: 400, fontSize: 12, color: '#7f7f7f' }}> (set by an admin)</span>}</label>
            <div style={styles.radioGroup}>
              <label style={styles.radioLabel}>
                <input
                  type="radio"
                  name="leadSource"
                  value="agent-generated"
                  checked={formData.leadSource === 'agent-generated'}
                  onChange={handleChange}
                  disabled={userRole !== 'Admin'} /* only admins change Lead generated by */
                />
                Agent
              </label>
              <label style={styles.radioLabel}>
                <input
                  type="radio"
                  name="leadSource"
                  value="team-generated"
                  checked={formData.leadSource === 'team-generated'}
                  onChange={handleChange}
                  disabled={userRole !== 'Admin'} /* only admins change Lead generated by */
                />
                Team
              </label>
            </div>
          </div>
        </fieldset>

        {formData.propertyStatus === 'new-construction' && (
          <fieldset style={styles.fieldset}>
            <legend style={styles.legend}>Builder Bonus</legend>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Is there a builder bonus?</label>
              <div style={styles.radioGroup}>
                <label style={styles.radioLabel}>
                  <input type="radio" name="builderBonusType" value="percentage" checked={formData.builderBonusType === 'percentage'} onChange={handleChange} required />
                  Percentage
                </label>
                <label style={styles.radioLabel}>
                  <input type="radio" name="builderBonusType" value="flat" checked={formData.builderBonusType === 'flat'} onChange={handleChange} required />
                  Flat amount
                </label>
                <label style={styles.radioLabel}>
                  <input type="radio" name="builderBonusType" value="none" checked={formData.builderBonusType === 'none'} onChange={handleChange} required />
                  No bonus
                </label>
              </div>
            </div>
            {formData.builderBonusType === 'percentage' && (
              <div style={styles.inputGroup}>
                <label style={styles.label}>Builder Bonus (%)</label>
                <input type="number" step="0.01" name="builderBonusPercent" value={formData.builderBonusPercent} onChange={handleChange} placeholder="e.g., 1" style={styles.input} />
              </div>
            )}
            {formData.builderBonusType === 'flat' && (
              <div style={styles.inputGroup}>
                <label style={styles.label}>Builder Bonus Amount ($)</label>
                <MoneyInput name="builderBonusAmount" value={formData.builderBonusAmount} onChange={handleChange} style={styles.input} />
              </div>
            )}
          </fieldset>
        )}


        {/* Additional Notes */}
        <fieldset style={styles.fieldset}>
          <legend style={styles.legend}>Additional Notes</legend>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Additional Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              style={styles.textarea}
              rows="4"
            />
          </div>
        </fieldset>

        {/* Submit Buttons */}
        <div style={styles.buttonGroup}>
          <button
            type="submit"
            disabled={loading}
            style={{...styles.submitButton, opacity: loading ? 0.6 : 1}}
          >
            {loading ? 'Saving...' : 'Save Transaction'}
          </button>
          {(!isEdit || wasDraft) && (
            <button type="button" onClick={saveDraft} disabled={loading || draftSaving} style={{ ...styles.cancelButton, opacity: draftSaving ? 0.6 : 1 }}>
              {draftSaving ? 'Saving draft...' : 'Save as draft'}
            </button>
          )}
          <button
            type="button"
            onClick={handleCancel}
            style={styles.cancelButton}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '900px',
    margin: '0 auto',
    padding: '24px',
    backgroundColor: '#FFFFFF',
    minHeight: '100vh',
    color: '#000000'
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    backgroundColor: '#F3F4F6',
    color: '#000000',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginBottom: '24px',
    fontSize: '14px',
    fontWeight: 'bold'
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    marginBottom: '24px',
    color: '#B8860B'
  },
  errorBox: {
    backgroundColor: '#FDECEC',
    border: '1px solid #D32F2F',
    borderRadius: '4px',
    padding: '16px',
    marginBottom: '24px'
  },
  errorText: {
    color: '#8B0000',
    margin: '0',
    fontSize: '14px'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  },
  fieldset: {
    border: '1px solid #DDDDDD',
    borderRadius: '4px',
    padding: '16px',
    backgroundColor: '#FFFFFF'
  },
  subheading: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#B8860B',
    margin: '8px 0 12px'
  },
  legend: {
    color: '#B8860B',
    fontSize: '16px',
    fontWeight: 'bold',
    padding: '0 8px'
  },
  inputGroup: {
    marginBottom: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#444444'
  },
  input: {
    padding: '10px 12px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #CCCCCC',
    borderRadius: '4px',
    color: '#000000',
    fontSize: '14px',
    fontFamily: 'inherit'
  },
  textarea: {
    padding: '10px 12px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #CCCCCC',
    borderRadius: '4px',
    color: '#000000',
    fontSize: '14px',
    fontFamily: 'inherit',
    resize: 'vertical'
  },
  twoColumn: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px'
  },
  checkboxGroup: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '12px'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: '#666666',
    cursor: 'pointer'
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: '#666666',
    cursor: 'pointer'
  },
  buttonGroup: {
    display: 'flex',
    gap: '16px',
    justifyContent: 'center',
    marginTop: '24px'
  },
  submitButton: {
    padding: '12px 32px',
    backgroundColor: '#D4AF37',
    color: '#000000',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer'
  },
  cancelButton: {
    padding: '12px 32px',
    backgroundColor: '#E5E7EB',
    color: '#111111',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer'
  }
};
